

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import QuestionFetcher from "./QuestionFetcher" // Import QuestionFetcher
import QuizFormatter from "./QuizFormatter" // Import QuizFormatter
import { Helmet } from "react-helmet"

//Const for meta
const pageUrl = 'https://www.aiatedu.com/quiz-generator'
const pageTitle = 'Quiz Generator | AIAT'
const pageDesc =
  'Generate custom quizzes for A-level, AP, and IB exams with AIAT’s Quiz Generator. Choose your quiz type, topics, and get instant feedback or detailed solutions.'
// Syllabus code mapping by subject and exam board
const SYLLABUS_CODES = {
  Mathematics: {
    CAIE: ["9709", "0580", "0606", "0607", "0980", "9231"],
    Edexcel: ["4MA1", "9MA0", "4PM0", "4PM1", "9FM0"],
    AP: ["Calculus AB", "Calculus BC", "Statistics"],
  },
  Physics: {
    CAIE: ["9702", "0625", "0972", "5054"],
    Edexcel: ["4PH1", "9PH0"],
    AP: ["Physics 1", "Physics 2", "Physics C: Mechanics", "Physics C: E&M"],
  },
  Chemistry: {
    CAIE: ["9701", "0620", "0971", "0971"],
    Edexcel: ["4CH1", "9CH0"],
    AP: ["Chemistry"],
  },
  Biology: {
    CAIE: ["9700", "0610", "0970", "0610"],
    Edexcel: ["4BI1", "9BI0"],
    AP: ["Biology"],
  },
  "Computer Science": {
    CAIE: ["9618", "0478", "0984", "9210"],
    Edexcel: ["4CP1", "9CP0"],
    AP: ["Computer Science A", "Computer Science Principles"],
  },
}

// Step transition messages
const STEP_MESSAGES = {
  1: {
    title: "Great choice!",
    message: "Now let's determine how long your assessment should be.",
    icon: "fa-thumbs-up",
  },
  2: {
    title: "Perfect timing!",
    message: "Next, select the subject you want to focus on.",
    icon: "fa-clock",
  },
  3: {
    title: "Excellent subject choice!",
    message: "Now, let's specify the exam board and syllabus code.",
    icon: "fa-book",
  },
  4: {
    title: "Almost there!",
    message: "Would you like to include solutions with your questions?",
    icon: "fa-check-circle",
  },
  5: {
    title: "Great decision!",
    message: "Let's add some details to personalize your assessment.",
    icon: "fa-user-edit",
  },
  6: {
    title: "Final step!",
    message: "Now, add topics to generate your personalized quiz.",
    icon: "fa-star",
  },
  7: {
    title: "All set!",
    message: "Your quiz is ready to be generated.",
    icon: "fa-trophy",
  },
}

const QuizSetupWizard = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [showStepMessage, setShowStepMessage] = useState(false)
  const [selections, setSelections] = useState({
    quizType: "",
    timeLength: "",
    subject: "",
    examBoard: "",
    syllabusCode: "",
    includeSolution: false,
    examName: "",
    schoolName: "",
    authorName: "",
    topics: [],
  })
  const [availableOptions, setAvailableOptions] = useState({
    subjects: Object.keys(SYLLABUS_CODES),
    examBoards: ["CAIE", "Edexcel", "AP"],
    syllabusCodes: [],
  })
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [questionCount, setQuestionCount] = useState(0)
  const [requiredQuestions, setRequiredQuestions] = useState(0)

  // Update available syllabus codes when subject or exam board changes
  useEffect(() => {
    if (selections.subject && selections.examBoard) {
      const codes = SYLLABUS_CODES[selections.subject]?.[selections.examBoard] || []
      setAvailableOptions((prev) => ({
        ...prev,
        syllabusCodes: codes,
      }))

      // Reset syllabus code if current selection is not valid for new subject/exam board
      if (!codes.includes(selections.syllabusCode)) {
        setSelections((prev) => ({
          ...prev,
          syllabusCode: "",
        }))
      }
    } else {
      setAvailableOptions((prev) => ({
        ...prev,
        syllabusCodes: [],
      }))
    }
  }, [selections.subject, selections.examBoard])

  // Calculate required questions based on time
  useEffect(() => {
    if (selections.timeLength) {
      setRequiredQuestions(QuizFormatter.TIME_TO_QUESTIONS[selections.timeLength] || 12)
    }
  }, [selections.timeLength])

  // Search for questions when topics change
  useEffect(() => {
    if (selections.topics.length > 0) {
      searchQuestions()
    }
  }, [selections.topics])

  const searchQuestions = async () => {
    setIsSearching(true)
    const searchText = selections.topics.join(" ")

    try {
      // Prepare filters based on selections
      const filters = {}
      if (selections.subject) {
        filters.subject = selections.subject
      }
      if (selections.examBoard) {
        filters.examBoard = selections.examBoard
      }
      if (selections.syllabusCode) {
        filters.syllabusCode = selections.syllabusCode
      }

      // Use the new search with fallback method
      const searchResult = await QuestionFetcher.searchWithFallback(searchText, filters)

      setSearchResults(searchResult.questions)
      setQuestionCount(searchResult.questions.length)

      // Log information about the search results
      if (searchResult.fallbackUsed) {
        console.log(
          `Filters applied but no results found. Showing ${searchResult.totalFound} unfiltered results instead.`,
        )
      } else if (searchResult.usedFilters) {
        console.log(`Found ${searchResult.filteredCount} questions after applying filters.`)
      } else {
        console.log(`Found ${searchResult.totalFound} questions from search.`)
      }
    } catch (error) {
      console.error("Error searching questions:", error)
      setSearchResults([])
      setQuestionCount(0)
    } finally {
      setIsSearching(false)
    }
  }

  const nextStep = () => {
    setIsTransitioning(true)
    setShowStepMessage(true)

    // Show transition message for 1.5 seconds
    setTimeout(() => {
      setShowStepMessage(false)
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1)
        setIsTransitioning(false)
      }, 300)
    }, 1500)
  }

  const prevStep = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep((prev) => prev - 1)
      setIsTransitioning(false)
    }, 300)
  }

  const updateSelection = (key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }))
  }

  const generateQuiz = () => {
    // Randomize the search results before selecting questions
    const randomizedResults = QuestionFetcher.randomizeQuestions(searchResults)

    // Select the required number of questions from randomized results
    const selectedQuestions = randomizedResults.slice(0, requiredQuestions)

    // Use the updated QuizFormatter with proper options
    QuizFormatter.printFormattedQuiz(selectedQuestions, {
      includeSolution: selections.includeSolution,
      format: QuizFormatter.QUIZ_FORMATS[selections.quizType.toUpperCase()] || QuizFormatter.QUIZ_FORMATS.QUIZ,
      metadata: {
        examName: selections.examName,
        schoolName: selections.schoolName,
        authorName: selections.authorName,
      },
    })
  }

  // Render step transition message
  const renderStepMessage = () => {
    const message = STEP_MESSAGES[currentStep]
    if (!message || !showStepMessage) return null

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-8 max-w-md text-center transform scale-in-center animate-bounce">
          <div className="mb-4 text-5xl text-blue-600">
            <i className={`fas ${message.icon}`}></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">{message.title}</h3>
          <p className="text-gray-600">{message.message}</p>
          <div className="mt-4 flex justify-center">
            <div className="inline-block relative">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
          <Helmet>
            {/* 1. CMP */}
      <script
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
        data-settings-id="R7Xi1xeFRKhrKk"
        async
      />
      {/* 2. gtag */}
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=AW-17303305286"
      />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17303305286');
      `}</script>
      {/* 3a. GTM head */}
      <script>{`
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MBKBFC9M');
      `}</script>
        {/* Browser tab title */}
        <title>{pageTitle}</title>

        {/* Basic SEO */}
        <meta name="description" content={pageDesc} />
        <meta
          name="keywords"
          content="quiz generator, A-level quiz, AP quiz, IB quiz, AI quiz, practice test"
        />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-center mb-4">
            <div className="flex space-x-4">
              {[1, 2, 3, 4, 5, 6, 7].map((step) => (
                <div
                  key={step}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    step <= currentStep ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / 7) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div
          className={`transition-all duration-300 ${
            isTransitioning ? "opacity-0 transform scale-95" : "opacity-100 transform scale-100"
          }`}
        >
          {currentStep === 1 && (
            <QuizTypeStep
              selection={selections.quizType}
              onSelect={(value) => updateSelection("quizType", value)}
              onNext={nextStep}
            />
          )}
          {currentStep === 2 && (
            <TimeLengthStep
              selection={selections.timeLength}
              onSelect={(value) => updateSelection("timeLength", value)}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 3 && (
            <SubjectStep
              selection={selections.subject}
              options={availableOptions.subjects}
              onSelect={(value) => updateSelection("subject", value)}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 4 && (
            <ExamBoardStep
              examBoard={selections.examBoard}
              syllabusCode={selections.syllabusCode}
              examBoardOptions={availableOptions.examBoards}
              syllabusOptions={availableOptions.syllabusCodes}
              onSelectExamBoard={(value) => updateSelection("examBoard", value)}
              onSelectSyllabus={(value) => updateSelection("syllabusCode", value)}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 5 && (
            <SolutionStep
              selection={selections.includeSolution}
              onSelect={(value) => updateSelection("includeSolution", value)}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 6 && (
            <MetadataStep
              examName={selections.examName}
              schoolName={selections.schoolName}
              authorName={selections.authorName}
              onUpdateExamName={(value) => updateSelection("examName", value)}
              onUpdateSchoolName={(value) => updateSelection("schoolName", value)}
              onUpdateAuthorName={(value) => updateSelection("authorName", value)}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 7 && (
            <TopicsStep
              topics={selections.topics}
              onUpdateTopics={(topics) => updateSelection("topics", topics)}
              questionCount={questionCount}
              requiredQuestions={requiredQuestions}
              isSearching={isSearching}
              onGenerateQuiz={generateQuiz}
              onPrev={prevStep}
              selections={selections}
            />
          )}
        </div>
      </div>
      {renderStepMessage()}
    </div>
    </>
  )
}

const QuizTypeStep = ({ selection, onSelect, onNext }) => {
  const options = [
    {
      value: "quiz",
      title: "Quiz",
      description: "Quick assessment with immediate feedback",
      icon: "fa-clipboard-question",
      color: "from-green-400 to-green-600",
    },
    {
      value: "practice",
      title: "Practice",
      description: "Practice session with detailed solutions",
      icon: "fa-dumbbell",
      color: "from-blue-400 to-blue-600",
    },
    {
      value: "exam",
      title: "Exam",
      description: "Formal examination simulation like Real Papers",
      icon: "fa-graduation-cap",
      color: "from-purple-400 to-purple-600",
    },
  ]

  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Choose Quiz Type</h2>
      <p className="text-gray-600 mb-8">What type of assessment would you like to create?</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {options.map((option) => (
          <div
            key={option.value}
            onClick={() => onSelect(option.value)}
            className={`cursor-pointer transform transition-all duration-300 hover:scale-105 ${
              selection === option.value ? "ring-4 ring-blue-500" : ""
            }`}
          >
            <div className={`bg-gradient-to-br ${option.color} rounded-xl p-6 text-white shadow-lg`}>
              <i className={`fas ${option.icon} text-4xl mb-4`}></i>
              <h3 className="text-xl font-bold mb-2">{option.title}</h3>
              <p className="text-sm opacity-90">{option.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>
          Choose "Quiz" for quick assessments, "Practice" for learning with solutions, or "Exam" for formal testing.
        </p>
      </div>

      <button
        onClick={onNext}
        disabled={!selection}
        className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
          selection
            ? "bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-105"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        Continue
      </button>
    </div>
  )
}

const TimeLengthStep = ({ selection, onSelect, onNext, onPrev }) => {
  const options = Object.keys(QuizFormatter.TIME_TO_QUESTIONS).map((time) => ({
    value: time,
    title: time,
    questions: QuizFormatter.TIME_TO_QUESTIONS[time],
  }))

  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Select Duration</h2>
      <p className="text-gray-600 mb-8">How long should your quiz be?</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {options.map((option) => (
          <div
            key={option.value}
            onClick={() => onSelect(option.value)}
            className={`cursor-pointer p-4 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
              selection === option.value ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="text-lg font-semibold text-gray-800">{option.title}</div>
            <div className="text-sm text-gray-500">{option.questions} questions</div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>The duration determines how many questions will be included in your assessment.</p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!selection}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            selection ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  )
}

const SubjectStep = ({ selection, options, onSelect, onNext, onPrev }) => {
  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Choose Subject</h2>
      <p className="text-gray-600 mb-8">Which subject would you like to focus on?</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {options.map((subject) => (
          <div
            key={subject}
            onClick={() => onSelect(subject)}
            className={`cursor-pointer p-6 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
              selection === subject ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="text-lg font-semibold text-gray-800">{subject}</div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>Your subject selection will determine available exam boards and syllabus codes in the next step.</p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!selection}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            selection ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  )
}

const ExamBoardStep = ({
  examBoard,
  syllabusCode,
  examBoardOptions,
  syllabusOptions,
  onSelectExamBoard,
  onSelectSyllabus,
  onNext,
  onPrev,
}) => {
  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Exam Board & Syllabus</h2>
      <p className="text-gray-600 mb-8">Select your exam board and syllabus code</p>

      <div className="space-y-6 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Exam Board</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {examBoardOptions.map((board) => (
              <div
                key={board}
                onClick={() => onSelectExamBoard(board)}
                className={`cursor-pointer p-4 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
                  examBoard === board ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="font-semibold text-gray-800">{board}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Syllabus Code</h3>
          {syllabusOptions.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {syllabusOptions.map((code) => (
                <div
                  key={code}
                  onClick={() => onSelectSyllabus(code)}
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
                    syllabusCode === code ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="font-semibold text-gray-800">{code}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 italic">
              {examBoard ? "No syllabus codes available for this selection" : "Please select an exam board first"}
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>Selecting a specific syllabus code will help find the most relevant questions for your assessment.</p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!examBoard}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            examBoard ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  )
}

const SolutionStep = ({ selection, onSelect, onNext, onPrev }) => {
  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Include Solutions?</h2>
      <p className="text-gray-600 mb-8">Would you like to include detailed solutions in your quiz?</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 max-w-2xl mx-auto">
        <div
          onClick={() => onSelect(true)}
          className={`cursor-pointer p-6 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
            selection === true ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <i className="fas fa-check-circle text-3xl text-green-500 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Yes, Include Solutions</h3>
          <p className="text-sm text-gray-600">Perfect for practice and learning</p>
        </div>

        <div
          onClick={() => onSelect(false)}
          className={`cursor-pointer p-6 rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
            selection === false ? "border-red-500 bg-red-50" : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <i className="fas fa-times-circle text-3xl text-red-500 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No, Questions Only</h3>
          <p className="text-sm text-gray-600">Ideal for exams and assessments</p>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>
          Include solutions for study materials and practice sessions. Exclude them for formal assessments and exams.
        </p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={selection === ""}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            selection !== ""
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  )
}

// New Metadata Step
const MetadataStep = ({
  examName,
  schoolName,
  authorName,
  onUpdateExamName,
  onUpdateSchoolName,
  onUpdateAuthorName,
  onNext,
  onPrev,
}) => {
  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Assessment Details</h2>
      <p className="text-gray-600 mb-8">Add custom information to personalize your assessment (optional)</p>

      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="space-y-6">
          <div>
            <label htmlFor="examName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Assessment Name
            </label>
            <input
              type="text"
              id="examName"
              value={examName}
              onChange={(e) => onUpdateExamName(e.target.value)}
              placeholder="e.g., Midterm Examination, Chapter 5 Quiz"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              School/Institution Name
            </label>
            <input
              type="text"
              id="schoolName"
              value={schoolName}
              onChange={(e) => onUpdateSchoolName(e.target.value)}
              placeholder="e.g., Springfield High School"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Author/Teacher Name
            </label>
            <input
              type="text"
              id="authorName"
              value={authorName}
              onChange={(e) => onUpdateAuthorName(e.target.value)}
              placeholder="e.g., Ms. Johnson"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>
          Adding these details will personalize your assessment. All fields are optional - you can leave them blank if
          you prefer.
        </p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-8 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300"
        >
          Continue
        </button>
      </div>
    </div>
  )
}

// Dummy progress bar component for search
const SearchProgressBar = ({ isSearching }) => {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let interval
    if (isSearching) {
      setProgress(0)
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          // Slow down as we approach 90%
          const increment = prevProgress < 30 ? 5 : prevProgress < 60 ? 3 : prevProgress < 80 ? 1 : 0.5
          const newProgress = Math.min(prevProgress + increment, 90)
          return newProgress
        })
      }, 100)
    } else {
      // Complete the progress bar when search is done
      setProgress(100)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isSearching])

  return (
    <div className="w-full mb-4">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-blue-700">Searching questions...</span>
        <span className="text-sm font-medium text-blue-700">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>Analyzing topics</span>
        <span>Filtering results</span>
        <span>Finalizing</span>
      </div>
    </div>
  )
}

const TopicsStep = ({
  topics,
  onUpdateTopics,
  questionCount,
  requiredQuestions,
  isSearching,
  onGenerateQuiz,
  onPrev,
  selections,
}) => {
  const [newTopic, setNewTopic] = useState("")

  const addTopic = () => {
    if (newTopic.trim() && !topics.includes(newTopic.trim())) {
      onUpdateTopics([...topics, newTopic.trim()])
      setNewTopic("")
    }
  }

  const removeTopic = (topicToRemove) => {
    onUpdateTopics(topics.filter((topic) => topic !== topicToRemove))
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      addTopic()
    }
  }

  const isEnoughQuestions = questionCount >= requiredQuestions
  const canGenerate = topics.length > 0 && isEnoughQuestions

  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Select Topics</h2>
      <p className="text-gray-600 mb-8">Add topics you want to include in your {selections.quizType}</p>

      {/* Quiz Summary */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quiz Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Type:</span>
            <div className="font-semibold capitalize">{selections.quizType}</div>
          </div>
          <div>
            <span className="text-gray-500">Duration:</span>
            <div className="font-semibold">{selections.timeLength}</div>
          </div>
          <div>
            <span className="text-gray-500">Subject:</span>
            <div className="font-semibold">{selections.subject}</div>
          </div>
          <div>
            <span className="text-gray-500">Exam Board:</span>
            <div className="font-semibold">{selections.examBoard}</div>
          </div>
          <div>
            <span className="text-gray-500">Syllabus:</span>
            <div className="font-semibold">{selections.syllabusCode || "Not specified"}</div>
          </div>
          <div>
            <span className="text-gray-500">Solutions:</span>
            <div className="font-semibold">{selections.includeSolution ? "Yes" : "No"}</div>
          </div>
          {selections.examName && (
            <div>
              <span className="text-gray-500">Assessment Name:</span>
              <div className="font-semibold">{selections.examName}</div>
            </div>
          )}
          {selections.schoolName && (
            <div>
              <span className="text-gray-500">School:</span>
              <div className="font-semibold">{selections.schoolName}</div>
            </div>
          )}
          {selections.authorName && (
            <div>
              <span className="text-gray-500">Author:</span>
              <div className="font-semibold">{selections.authorName}</div>
            </div>
          )}
        </div>
      </div>

      {/* Topic Input */}
      <div className="mb-6">
        <div className="flex max-w-md mx-auto">
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter a topic (e.g., quadratic equations, calculus)"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={addTopic}
            className="px-6 py-3 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-all duration-300"
          >
            Add
          </button>
        </div>
      </div>

      {/* Selected Topics */}
      {topics.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-700 mb-3">Selected Topics:</h4>
          <div className="flex flex-wrap justify-center gap-2">
            {topics.map((topic, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
              >
                {topic}
                <button onClick={() => removeTopic(topic)} className="ml-2 text-blue-600 hover:text-blue-800">
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search Progress Bar */}
      {isSearching && <SearchProgressBar isSearching={isSearching} />}

      {/* Question Count Status */}
      {topics.length > 0 && !isSearching && (
        <div className="mb-6">
          <div
            className={`p-4 rounded-lg ${isEnoughQuestions ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"}`}
          >
            <div className="flex items-center justify-center">
              <i
                className={`fas ${isEnoughQuestions ? "fa-check-circle text-green-600" : "fa-exclamation-triangle text-yellow-600"} mr-2`}
              ></i>
              <span className={isEnoughQuestions ? "text-green-800" : "text-yellow-800"}>
                Found {questionCount} questions (need {requiredQuestions})
              </span>
            </div>
            {!isEnoughQuestions && questionCount > 0 && (
              <p className="text-sm text-yellow-700 mt-2">
                Add more topics or reduce the quiz duration to get enough questions
              </p>
            )}
            {questionCount === 0 && topics.length > 0 && (
              <p className="text-sm text-yellow-700 mt-2">
                No questions found for these topics. Try different or more general topics.
              </p>
            )}
            {/* Add feedback about filter fallback */}
            {questionCount > 0 && (selections.subject || selections.examBoard || selections.syllabusCode) && (
              <p className="text-sm text-blue-700 mt-2">
                <i className="fas fa-info-circle mr-1"></i>
                Results include questions matching your topics. Filters are applied when possible.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 text-blue-700">
        <h4 className="font-bold">Tip:</h4>
        <p>
          Add multiple topics to increase the variety of questions. Be specific for targeted practice or general for
          broader coverage.
        </p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrev}
          className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={onGenerateQuiz}
          disabled={!canGenerate}
          className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            canGenerate
              ? "bg-green-600 text-white hover:bg-green-700 transform hover:scale-105"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          <i className="fas fa-file-pdf mr-2"></i>
          Generate Quiz
        </button>
      </div>
    </div>
  )
}

export default QuizSetupWizard
